import { HTTPError, TimeoutError } from 'ky';
import { isArray, isNullish, isObjectType, isString, unique } from 'remeda';
import { toast } from 'sonner';

import {
  DEFAULT_LIMIT,
  FILTER_ACTION_TYPES,
  FILTER_KEYS,
  INITIAL_FILTER,
  LIMIT_OPTIONS,
  SLOW_QUERY_MESSAGE,
  SPIN_RESULT_PROVIDERS,
} from './constants';

import { ProviderName } from 'v2/constants';
import {
  ProviderTransaction,
  ProviderTransactionActionType,
  ProviderTransactionFilter,
} from 'v2/types/ProviderTransactionModel';

const PROVIDER_NAMES: string[] = Object.values(ProviderName);

export function parseFilter(searchParams: URLSearchParams): ProviderTransactionFilter {
  const filter = FILTER_KEYS.reduce<ProviderTransactionFilter>(
    (acc, key) => ({ ...acc, [key]: searchParams.get(key) ?? '' }),
    INITIAL_FILTER
  );

  // An unknown provider is rejected by the backend with 400, so it is dropped right away;
  // an action type the select does not offer would leave the select blank.
  return {
    ...filter,
    providerName: PROVIDER_NAMES.includes(filter.providerName) ? filter.providerName : '',
    actionType: FILTER_ACTION_TYPES.includes(filter.actionType) ? filter.actionType : '',
  };
}

export function parseLimit(value: string | null): number {
  const limit = Number(value);

  return LIMIT_OPTIONS.some((option) => option.value === limit) ? limit : DEFAULT_LIMIT;
}

export function toSearchParams(filter: ProviderTransactionFilter, limit: number): URLSearchParams {
  const searchParams = new URLSearchParams();

  FILTER_KEYS.forEach((key) => {
    if (filter[key]) {
      searchParams.set(key, filter[key]);
    }
  });

  if (limit !== DEFAULT_LIMIT) {
    searchParams.set('limit', String(limit));
  }

  return searchParams;
}

export function sanitizeUserSearch(value: string): string {
  return value.replace(/,/g, '').trim().replace(/^@+/, '');
}

export function isSpinResultAmount(transaction: ProviderTransaction): boolean {
  return (
    transaction.actionType === ProviderTransactionActionType.BET &&
    SPIN_RESULT_PROVIDERS.includes(transaction.providerName)
  );
}

export function formatNumber(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

export function formatAmount(transaction: ProviderTransaction): string | null {
  const { amount, actionType } = transaction;

  if (isNullish(amount)) {
    return null;
  }

  if (actionType === ProviderTransactionActionType.BET && !isSpinResultAmount(transaction) && amount > 0) {
    return `-${formatNumber(amount)}`;
  }

  return formatNumber(amount);
}

function getValidationFields(errors: unknown): string[] {
  if (!isArray(errors)) {
    return [];
  }

  return unique(
    errors.flatMap((error) => (isObjectType(error) && 'path' in error && isString(error.path) ? [error.path] : []))
  );
}

export async function handleProviderTransactionsError(error: unknown): Promise<void> {
  if (error instanceof TimeoutError) {
    toast.error(SLOW_QUERY_MESSAGE);
    return;
  }

  if (!(error instanceof HTTPError)) {
    toast.error('Не удалось загрузить спины');
    return;
  }

  if (error.response.status >= 500) {
    toast.error(SLOW_QUERY_MESSAGE);
    return;
  }

  const body: unknown = await error.response.json().catch(() => null);

  if (!isObjectType(body) || !('message' in body) || !isString(body.message)) {
    toast.error(`Не удалось загрузить спины (${error.response.status})`);
    return;
  }

  const fields = 'errors' in body ? getValidationFields(body.errors) : [];

  toast.error(fields.length > 0 ? `${body.message}: ${fields.join(', ')}` : body.message);
}
